import {
  type Clip,
  type Op,
  type Peer,
  type ServerMessage,
  type Timeline,
  type RasterWahl,
  type ZeitFormat,
  naechstesZeitFormat,
  type Warteschlange as WarteschlangeT,
  Warteschlange,
  anstellen,
  alteSchritte,
  aufraeumen,
  bestaetigt,
  leereSchlange,
  mitOffenem,
  offenSatz,
  serverStandUebernehmen,
  applyOp,
  applyOps,
  autoPegel,
  describeOps,
  emptyTimeline,
  invertOp,
  magnetZug,
  mitGruppe,
  ohneGesperrte,
  type Wellenform,
} from '@schnitt/shared';
import { create } from 'zustand';
import { api, getShareToken, type AssetView, type KommentarView, type ProjectDetail, type QueueState, type RenderView } from './api';

/**
 * One store per open project.
 *
 * Edits are applied locally first and sent to the server, which decides the
 * order and echoes them back. The local copy is therefore always ahead of the
 * confirmed revision by whatever is still in flight; when the server's version
 * of an operation arrives it is applied on top, and because operations are
 * written to converge under any order, the two ends agree again without a
 * merge step.
 */

interface Toast {
  id: number;
  kind: 'info' | 'error' | 'success';
  message: string;
}

export type ConnectionState = 'connecting' | 'live' | 'offline';

/**
 * One step of history: the operations that reverse a change, plus the change's
 * name in words. The name is what the undo button shows -- an arrow alone does
 * not tell anyone whether it is about to take back a trim or a deleted clip.
 */
export interface HistoryEntry {
  ops: Op[];
  label: string;
}

/** Was nach dem Aussuchen auf seine Stelle wartet (17. Oktober). */
export type Platzieren =
  | { art: 'geraet'; dateien: File[]; namen: string[] }
  | { art: 'medien'; assetIds: string[]; namen: string[] };

interface EditorState {
  project: ProjectDetail | null;
  role: 'owner' | 'editor' | 'viewer';
  timeline: Timeline;
  rev: number;
  assets: AssetView[];
  renders: RenderView[];
  /** Hinweise, die an einer Stelle im Schnitt hängen. */
  kommentare: KommentarView[];
  /**
   * Wie hoch eine Spur steht, in Pixeln. Gehört zum Gerät, nicht zum Schnitt:
   * am Telefon will man die Leinwand groß, am großen Schirm die Spuren.
   * Der Griff in der Transportleiste zieht daran (7.35).
   */
  spurHoehe: number;
  /**
   * Bildspuren, deren Tonkurve aufgeklappt ist (7.65).
   *
   * Sascha, 8. September: „wir brauchen eine Ebene, also ein Video
   * ausklappbar für Schnitt nach Audio. D.h. wir müssen da die Audio
   * Kennlinien sehen.“ Ein Video hat seinen Ton im selben Clip -- sichtbar
   * war davon nur ein schmales Band unter dem Filmstreifen, zu wenig, um
   * danach zu schneiden.
   *
   * Das ist eine Ansicht und kein Schnitt: es steht im Browser und nicht im
   * Dokument. Wer neben jemandem arbeitet, klappt auf, was *er* braucht.
   */
  tonkurven: string[];
  tonkurveUmschalten: (trackId: string) => void;
  /** Nachrücken beim Kürzen (S1): rutscht alles hinter dem Clip mit? */
  nachruecken: boolean;
  nachrueckenUmschalten: () => void;
  /** Was dauerhaft auf der Leinwand liegt (S25) -- Drittel, Schutzbereich, beides. */
  raster: RasterWahl;
  setRaster: (wahl: RasterWahl) => void;
  /**
   * Der Pegelmesser neben der Vorschau: an, aus, oder `null` für die Vorgabe
   * -- am grossen Schirm an, am Telefon aus. Gehört dem Gerät, wie das Raster.
   */
  pegelWahl: 'an' | 'aus' | null;
  setPegelWahl: (wahl: 'an' | 'aus') => void;
  zeitformat: ZeitFormat;
  zeitformatWeiter: () => void;
  /**
   * Der Clip, an dem der nächste Hinweis kleben soll -- gesetzt vom
   * Kontextmenü am Clip, gelesen vom Hinweis-Blatt (Sascha: „Auch am Objekt
   * entstehen“). Er gehört nicht in das Blatt selbst: gewählt wird er
   * draussen, und das Blatt ist zu dem Zeitpunkt noch gar nicht offen.
   */
  hinweisAnker: string | null;
  setHinweisAnker: (clipId: string | null) => void;
  /**
   * Der gewählte Bereich (S5) -- oder `null`, wenn der ganze Schnitt gilt.
   *
   * Er steht im Speicher dieser Sitzung und nicht am Projekt: ein Bereich ist
   * die Absicht für den *nächsten* Export, nicht eine Eigenschaft des
   * Schnitts. Am Projekt stünde er für alle, die daran sitzen, und wer morgen
   * das Projekt öffnet, exportierte aus Versehen einen Ausschnitt.
   */
  bereich: { von: number; bis: number } | null;
  setBereich: (bereich: { von: number; bis: number } | null) => void;
  /** Die zuletzt gezogene volle Höhe -- ein Tipp auf den Griff holt sie zurück. */
  spurHoeheGross: number;
  setSpurHoehe: (px: number) => void;
  /** Ein Tipp auf den Griff: flach machen, oder die volle Höhe zurückholen. */
  zeitleisteUmschalten: () => void;
  queue: QueueState;
  peers: Peer[];
  connection: ConnectionState;
  /** Die Verbindung wurde geschlossen, weil der Zugang weg ist (7.280). */
  zugangWeg: boolean;
  /**
   * Was noch nicht beim Server ist (S28). Null, solange kein Projekt offen
   * ist; sonst immer eine Schlange, auch eine leere -- „keine Schlange“ und
   * „leere Schlange“ auseinanderzuhalten kostet an jeder Stelle eine Frage.
   */
  offen: WarteschlangeT | null;
  /** Der Satz dazu, oder null. Für die Leiste, die es anzeigt. */
  offenSatz: string | null;
  /**
   * Was älter als zwölf Stunden in der Schlange liegt (7.220). Solange das
   * steht, geht nichts hinaus -- weder das Alte noch Neues dahinter, damit die
   * Reihenfolge stimmt --, und der Schnittplatz fragt: nachtragen oder
   * verwerfen.
   */
  alteOffen: { schritte: number; seit: number } | null;
  alteNachtragen: () => void;
  alteVerwerfen: () => Promise<void>;
  selection: string[];
  /** Taps add to the selection instead of replacing it. */
  multiSelect: boolean;
  /**
   * Der Übergangs-Modus (7.74e).
   *
   * Sascha, 10. September: „Die UI wo die Übergänge hinkommen ist echt
   * schlecht 😃 das blickt keiner und funktioniert nicht. Wir müssten
   * vielleicht n kleines + haben zwischen zwei Tracks. Wenn wir übergänge
   * gedrückt haben oderso...“ Genau das: der Knopf öffnet kein Blatt mehr,
   * das auf einen Schnitt zeigt, den man nicht sieht -- er macht die
   * Schnitte selbst gross und anfassbar. Erst der Tipp auf einen davon
   * öffnet die Auswahl, und dann für genau diesen Schnitt.
   */
  uebergangsModus: boolean;
  setUebergangsModus: (on: boolean) => void;
  /**
   * Der angetippte Keyframe -- der Clip und die Stelle in ihm.
   *
   * Ohne ihn war ein Keyframe nur zu sehen, nicht zu wählen: ein Tipp darauf
   * setzte den Abspielkopf, und die Rücktaste löschte danach den ganzen Clip
   * (Sascha, 7. September: „verschwindet einfach das ganze Element“). Steht
   * hier einer, nimmt die Rücktaste ihn -- und sonst nichts.
   */
  keyframeWahl: { clipId: string; at: number } | null;
  waehleKeyframe: (wahl: { clipId: string; at: number } | null) => void;
  /**
   * Wohin das nächste ausgesuchte Medium soll -- in Sekunden auf der
   * Zeitleiste, oder null für „hinten anfügen“.
   *
   * Das `+` steht am Ende der Spur, und das ist ehrlich: dort wird angefügt.
   * Nur ist das Ende bei einem langen Video weit weg (Sascha, 7. September:
   * „ein + Button ist irgendwie ganz weit weg, wenn das Video lang ist“).
   * Also gibt es ein zweites am Abspielkopf, und was man darüber aussucht,
   * landet genau dort.
   */
  einfuegeStelle: number | null;
  setEinfuegeStelle: (sekunde: number | null) => void;
  /**
   * Und auf welche Spur (7.215): wer das `+` in einer Zeile antippt, meint
   * diese Zeile. `null` heisst wie bisher: die erste Bild- bzw. Tonspur.
   */
  einfuegeSpur: string | null;
  /**
   * **Erst aussuchen, dann die Stelle** (Sascha, 17. Oktober: „Der Medien
   * Knopf könnte sowas wie Hinzufügen heißen … Die [Plus] könnten erst
   * kommen, wenn man die Medien ausgewählt hat und sich der Dialog wieder
   * schließt. Dann fragt es mit den + Zeichen, wo eingefügt werden soll“).
   * Solange hier etwas steht, zeigt die Zeitleiste ihre Plus, und ein Tipp
   * auf eines legt es dort ab.
   */
  platzieren: Platzieren | null;
  setPlatzieren: (p: Platzieren | null) => void;
  setEinfuegeSpur: (trackId: string | null) => void;
  /**
   * Das ausgesuchte Medium, das noch auf seine Stelle wartet (7.49).
   *
   * Zwischen „ausgesucht“ und „drin“ liegt jetzt eine Frage: wohin, auf welche
   * Ebene, und was bei einem Konflikt (Sascha, 7. September). Solange die
   * Antwort aussteht, steht das Medium hier.
   */
  einfuegeMedium: { assetId: string; name: string; kind: 'video' | 'audio' | 'image'; dauer: number } | null;
  setEinfuegeMedium: (m: { assetId: string; name: string; kind: 'video' | 'audio' | 'image'; dauer: number } | null) => void;
  /**
   * Was über das `+` aus der Mediathek kam und noch aufbereitet wird (7.156).
   * Es kommt in den Schnitt, sobald es fertig ist -- in der Reihenfolge der
   * Auswahl, am Marker von damals. `punkte` hält fest, wo auf welcher Spur
   * die nächste Datei hinkommt. Abgearbeitet wird in `einfuegeWarteschlange.ts`.
   */
  einfuegeWarteschlange: { ids: string[]; kopf: number; spur?: string | null; punkte: Record<string, number>; eingefuegt: number } | null;
  setEinfuegeWarteschlange: (
    w: { ids: string[]; kopf: number; spur?: string | null; punkte: Record<string, number>; eingefuegt: number } | null,
  ) => void;
  /**
   * Was gerade hochgeladen wird -- vom Blatt „Medien“ oder abgelegt mit der
   * Maus (15. Oktober). Steht im Laden und nicht im Blatt: eine Datei, die
   * auf der Zeitleiste landet, lädt hoch, ohne dass ein Blatt offen ist.
   */
  hochladen: { name: string; done: number; total: number; fraction: number } | null;
  setHochladen: (h: { name: string; done: number; total: number; fraction: number } | null) => void;
  /** Welche Spur das Blatt „Spur“ zeigt, wenn es offen ist (7.32a). */
  spurBlatt: string | null;
  setSpurBlatt: (trackId: string | null) => void;
  /** Welcher Exportbereich im Blatt „Bereich“ steht (7.166). */
  bereichBlatt: string | null;
  setBereichBlatt: (id: string | null) => void;
  /**
   * Der Clip, dessen Medium ersetzt werden soll (7.14).
   *
   * Steht er hier, zeigt das Blatt „Medien“ statt „In den Schnitt einfuegen“
   * ein „Ersetzen“ -- dieselbe Liste, andere Handlung. Ein zweites Blatt nur
   * zum Aussuchen waere dieselbe Liste noch einmal.
   */
  ersetzen: string | null;
  /**
   * Zwei kleine Fenster am Clip, die es bisher nur in der Zeitleiste gab:
   * Umbenennen und Angaben. Seit das Kontextmenü auch auf der Leinwand
   * aufgeht (7.61), muss beides von zwei Stellen aus zu öffnen sein --
   * gezeichnet werden sie weiterhin einmal, in der Zeitleiste.
   */
  clipUmbenennen: { clipId: string; name: string } | null;
  setClipUmbenennen: (wahl: { clipId: string; name: string } | null) => void;
  clipAngaben: string | null;
  setClipAngaben: (clipId: string | null) => void;
  setErsetzen: (clipId: string | null) => void;
  /**
   * Ein Griff, den die Leinwand angefangen hat und die Anfasser fortsetzen
   * sollen.
   *
   * Der erste Tipp ins Bild wählt die Ebene -- die Anfasser gibt es in
   * diesem Augenblick noch gar nicht, also könnten sie den Zug auch nicht
   * annehmen. Statt den Finger anheben und noch einmal aufsetzen zu lassen,
   * legt die Leinwand den angefangenen Griff hier ab; die Anfasser holen ihn
   * sich, sobald sie da sind (7.6a).
   */
  griff: { clipId: string; pointerId: number; x: number; y: number } | null;
  setGriff: (griff: { clipId: string; pointerId: number; x: number; y: number } | null) => void;
  /**
   * Die Lage der Form, solange an ihren Anfassern gezogen wird (7.224): die
   * Vorschau schneidet schon so, gespeichert wird beim Loslassen.
   */
  maskeEntwurf: { clipId: string; x: number; y: number; groesse: number; drehung: number } | null;
  setMaskeEntwurf: (entwurf: EditorState['maskeEntwurf']) => void;
  /** Copied clips, with the track they came from. In memory only. */
  clipboard: { clip: Clip; trackId: string }[];
  playhead: number;
  /**
   * **Überfahren mit der Maus** (7.207): solange gesetzt, steht der Kopf nur
   * vorübergehend dort, wo die Maus ist -- die Vorschau zeigt das Bild --,
   * und hier steht, wohin er zurückkehrt. `null`: kein Überfahren.
   */
  ueberfahrenVon: number | null;
  /** Den Kopf mit der Maus mitnehmen, ohne ihn zu setzen (7.207). */
  ueberfahren: (seconds: number) => void;
  /** Überfahren beenden: zurück an den alten Platz, oder (`behalten`) hier bleiben. */
  ueberfahrenEnde: (behalten: boolean) => void;
  playing: boolean;
  /** Playback runs silent while a voiceover is being recorded, so the
   *  speaker's own microphone does not pick up the cut. */
  monitorMuted: boolean;
  /** Pixels per second on the timeline. */
  zoom: number;
  toasts: Toast[];
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];

  open: (project: ProjectDetail, role: string, gastName?: string) => Promise<void>;
  close: () => void;
  dispatch: (ops: Op | Op[]) => void;
  undo: () => void;
  redo: () => void;
  select: (ids: string[]) => void;
  toggleSelect: (id: string) => void;
  setMultiSelect: (on: boolean) => void;
  copySelection: () => void;
  clearClipboard: () => void;
  setPlayhead: (seconds: number, broadcast?: boolean) => void;
  setPlaying: (playing: boolean) => void;
  setMonitorMuted: (muted: boolean) => void;
  setZoom: (zoom: number) => void;
  refreshAssets: () => Promise<void>;
  refreshKommentare: () => Promise<void>;
  /** Der letzte Abruf der Hinweise ging schief. Was da war, steht noch da. */
  kommentareFehler: boolean;
  /**
   * Der Name, der am Gast-Link steht -- leer, wenn keiner dran steht oder man
   * angemeldet ist. Dann fragt das Hinweis-Blatt nicht nach einem Namen, der
   * ohnehin nicht gilt (7.63).
   */
  gastName: string;
  /**
   * Wie viele Hinweise seit dem letzten Hinsehen dazugekommen sind und nicht
   * von einem selbst stammen. Rechnet der Server -- nur er weiß, wer fragt
   * (7.8d).
   */
  neueHinweise: number;
  hinweiseGesehen: () => Promise<void>;
  refreshRenders: () => Promise<void>;
  refreshProject: () => Promise<void>;
  toast: (kind: Toast['kind'], message: string) => void;
  dismissToast: (id: number) => void;
  clipById: (id: string) => { clip: Clip; trackId: string } | null;
}

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 1000;
let clientSeq = 0;
let presenceTimer: ReturnType<typeof setTimeout> | null = null;
let toastId = 0;

/**
 * Wie hoch eine Spur stehen darf, in Pixeln.
 *
 * `SPUR_FLACH` ist die Stellung, in der die Leinwand groß ist und die Spuren
 * nur noch Streifen sind -- alle drei bleiben da, sie werden flacher, nicht
 * weniger. Unter `SPUR_FLACH_BIS` gilt die Zeitleiste als eingeklappt.
 */
export const SPUR_MIN = 22;
export const SPUR_FLACH = 26;
export const SPUR_FLACH_BIS = 40;

/** Wie hoch die aufgeklappte Tonkurve einer Bildspur ist (7.65). */
export const TONKURVE_HOEHE = 28;

const TONKURVEN = 'schnitt.tonkurven';
function gespeicherteTonkurven(): string[] {
  try {
    const roh = localStorage.getItem(TONKURVEN);
    const liste: unknown = roh ? JSON.parse(roh) : [];
    return Array.isArray(liste) ? liste.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
/**
 * Was noch nicht beim Server ist, liegt im Browser (S28) -- je Projekt unter
 * eigenem Schlüssel: zwei offene Reiter auf zwei Schnitte dürfen sich nicht
 * ins Gehege kommen.
 *
 * Gelesen wird durch das Schema. Ein Eintrag, den eine ältere Fassung
 * geschrieben hat, wird verworfen statt geraten -- ein halb verstandener
 * Schritt auf einen fremden Schnitt anzuwenden ist schlimmer, als ihn zu
 * verlieren.
 */
const OFFEN_SCHLUESSEL = (projectId: string) => `schnitt.offen.${projectId}`;

/**
 * Wer dieser Browser für dieses Projekt ist, und bei welcher Nummer er steht
 * (7.101).
 *
 * Zusammen erkennt der Server ein Paket wieder, das er schon eingerechnet hat.
 * Gebraucht wird das für den einen Fall: das Paket kommt an, die Bestätigung
 * geht auf dem Rückweg verloren, und beim nächsten Verbinden geht es noch
 * einmal hinaus.
 *
 * **Kennung und Nummer gehören zusammen und müssen beide das Neuladen
 * überleben.** Beim ersten Anlauf lag nur die Kennung im Speicher, die Nummer
 * fing nach einem Neuladen wieder bei eins an -- und damit hielt der Server
 * *jedes* neue Paket für ein altes und wendete nichts mehr an. Der
 * Browser-Durchgang hat es in derselben Stunde gemeldet („nach dem Tipp steht
 * am Clip []“). Eine Wiedererkennung, die zu viel wiedererkennt, ist
 * schlimmer als keine.
 */
const KLIENT_SCHLUESSEL = (projectId: string) => `schnitt.klient.${projectId}`;

interface KlientStand {
  klient: string;
  seq: number;
}

function klientStand(projectId: string): KlientStand | null {
  try {
    const roh = localStorage.getItem(KLIENT_SCHLUESSEL(projectId));
    if (roh) {
      const gelesen = JSON.parse(roh) as Partial<KlientStand>;
      if (typeof gelesen?.klient === 'string' && Number.isInteger(gelesen?.seq)) {
        return { klient: gelesen.klient, seq: gelesen.seq! };
      }
    }
    // Sofort hinterlegen und nicht erst beim ersten Paket: sonst erfände jeder
    // Aufruf eine andere Kennung, und ein Paket aus der Warteschlange ginge
    // unter einer anderen hinaus als beim ersten Mal -- womit die
    // Wiedererkennung gerade dort versagte, wofür sie da ist.
    const frisch = { klient: `k-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`, seq: 0 };
    localStorage.setItem(KLIENT_SCHLUESSEL(projectId), JSON.stringify(frisch));
    return frisch;
  } catch {
    // Ohne Speicher gibt es keine Wiedererkennung -- das ist der Stand von
    // vorher und kein Grund, nicht zu arbeiten.
    return null;
  }
}

/** Die nächste Nummer für dieses Projekt, samt der Kennung, unter der sie gilt. */
function naechstesPaket(projectId: string): { seq: number; klient?: string } {
  const stand = klientStand(projectId);
  if (!stand) return { seq: ++clientSeq };
  const seq = Math.max(stand.seq, clientSeq) + 1;
  clientSeq = seq;
  try {
    localStorage.setItem(KLIENT_SCHLUESSEL(projectId), JSON.stringify({ klient: stand.klient, seq }));
  } catch {
    /* Privater Modus: dann gilt die Nummer nur für diese Sitzung. */
  }
  return { seq, klient: stand.klient };
}

/** Die Kennung allein -- für ein Paket, das schon eine Nummer hat. */
function klientId(projectId: string): string | undefined {
  return klientStand(projectId)?.klient;
}

function offenLesen(projectId: string): WarteschlangeT {
  try {
    const roh = localStorage.getItem(OFFEN_SCHLUESSEL(projectId));
    if (!roh) return leereSchlange(projectId);
    const geprueft = Warteschlange.safeParse(JSON.parse(roh));
    if (!geprueft.success || geprueft.data.projectId !== projectId) return leereSchlange(projectId);
    return geprueft.data;
  } catch {
    return leereSchlange(projectId);
  }
}

function offenSchreiben(schlange: WarteschlangeT): void {
  try {
    if (schlange.pakete.length === 0) localStorage.removeItem(OFFEN_SCHLUESSEL(schlange.projectId));
    else localStorage.setItem(OFFEN_SCHLUESSEL(schlange.projectId), JSON.stringify(schlange));
  } catch {
    /* Privater Modus: dann gilt die Schlange nur für diese Sitzung. */
  }
}

/**
 * Wer zuletzt **gefragt** hat, bestimmt -- nicht, wer zuletzt geantwortet hat
 * (7.113).
 *
 * Die drei Listen unten werden an mehreren Stellen geholt: beim Öffnen eines
 * Projekts, nach dem Schreiben, und auf ein Signal des Servers hin. Zwei
 * Abrufe sind damit regelmässig gleichzeitig unterwegs -- und ohne Marke
 * gewinnt die Antwort, die zufällig später eintrifft. Trifft die *ältere*
 * später ein, steht danach ein Stand da, den **niemand mehr korrigiert**:
 * der eigene Hinweis fehlt in der Liste, obwohl er längst auf dem Server
 * liegt, und er kommt erst wieder, wenn jemand das Blatt neu aufmacht.
 *
 * Das ist die zweite Hälfte von Saschas Meldung vom 8. September („Hinweise
 * werden nicht gespeichert und auch nicht angezeigt“). Die erste war der
 * stumme Fangarm, der die Liste leerte; der ist seit dem 8. September weg.
 * Diese hier war noch da.
 *
 * Die Marke zählt hoch. Eine Antwort, deren Marke nicht mehr die höchste ist,
 * wird weggeworfen -- die neuere Anfrage setzt den Stand, und die kommt
 * bestimmt.
 */
const abrufMarken: Record<string, number> = {};
function marke(was: string): () => boolean {
  const meine = (abrufMarken[was] = (abrufMarken[was] ?? 0) + 1);
  return () => abrufMarken[was] === meine;
}

const NACHRUECKEN = 'schnitt.nachruecken';
const RASTER = 'schnitt.raster';
const PEGEL = 'schnitt.pegelmesser';

function gespeichertePegelWahl(): 'an' | 'aus' | null {
  try {
    const roh = localStorage.getItem(PEGEL);
    if (roh === 'an' || roh === 'aus') return roh;
  } catch {
    /* ohne Speicher eben die Vorgabe */
  }
  return null;
}
const ZEITFORMAT = 'schnitt.zeitformat';

/**
 * Das Raster gehört dem Gerät und nicht dem Schnitt (S25): ob jemand die
 * Drittel sehen will, ist seine Arbeitsweise -- und wäre es eine Einstellung
 * des Projekts, sähen sie alle anderen auch.
 */
function gespeichertesRaster(): RasterWahl {
  try {
    const roh = localStorage.getItem(RASTER);
    if (roh === 'drittel' || roh === 'schutz' || roh === 'beides') return roh;
  } catch {
    /* Ein Browser ohne Speicher ist kein Grund, nicht zu arbeiten. */
  }
  return 'aus';
}
/**
 * Nachrücken ist eine Arbeitsweise und keine Eigenschaft des Schnitts: wer so
 * arbeitet, will es am nächsten Tag wieder so. Es liegt deshalb im Browser und
 * nicht am Projekt -- am Projekt stünde es für alle, die daran sitzen, und
 * zwei Leute können verschieden schneiden.
 *
 * Aus heisst aus: die alte Art ist die Vorgabe, weil sie die ist, die hier
 * bisher galt.
 */
function gespeichertesNachruecken(): boolean {
  try {
    return localStorage.getItem(NACHRUECKEN) === 'an';
  } catch {
    return false;
  }
}

/**
 * Das Zeitformat gehört dem Gerät (S31), aus demselben Grund wie das Raster:
 * ob jemand Bildnummern lesen will, ist seine Arbeitsweise und keine
 * Eigenschaft des Schnitts.
 *
 * Die Vorgabe ist der Timecode -- das war die Anzeige bisher, und wer nichts
 * umstellt, soll nichts Neues vorfinden.
 */
function gespeichertesZeitformat(): ZeitFormat {
  try {
    const roh = localStorage.getItem(ZEITFORMAT);
    if (roh === 'timecode' || roh === 'uhrzeit' || roh === 'bilder') return roh;
  } catch {
    /* Ein Browser ohne Speicher ist kein Grund, nicht zu arbeiten. */
  }
  return 'timecode';
}

export const SPUR_NORMAL = 46;
export const SPUR_MAX = 108;

function gespeicherteSpurHoehe(): number {
  try {
    const roh = localStorage.getItem('schnitt.spurhoehe');
    if (roh !== null) {
      const zahl = Number(roh);
      if (Number.isFinite(zahl)) return Math.min(SPUR_MAX, Math.max(SPUR_MIN, Math.round(zahl)));
    }
    // Was vor dem ziehbaren Griff gespeichert wurde: ein Ja/Nein.
    if (localStorage.getItem('schnitt.zeitleiste') === 'klein') return SPUR_FLACH;
  } catch {
    // Ein Browser ohne Speicher fängt eben jedes Mal bei der Normalhöhe an.
  }
  return SPUR_NORMAL;
}

/** Was die Hauptspur verändern kann -- nur danach muss der Magnet nachsehen. */
const MAGNET_ANLAESSE = new Set<Op['op']>(['addClip', 'removeClip', 'updateClip', 'moveClip', 'setTransition', 'removeTrack', 'updateSettings']);

export const useEditor = create<EditorState>((set, get) => ({
  project: null,
  role: 'viewer',
  timeline: emptyTimeline(),
  rev: 0,
  assets: [],
  renders: [],
  kommentare: [],
  kommentareFehler: false,
  gastName: '',
  neueHinweise: 0,
  spurHoehe: gespeicherteSpurHoehe(),
  tonkurven: gespeicherteTonkurven(),
  nachruecken: gespeichertesNachruecken(),
  raster: gespeichertesRaster(),
  pegelWahl: gespeichertePegelWahl(),
  zeitformat: gespeichertesZeitformat(),
  hinweisAnker: null,
  setHinweisAnker: (clipId) => set({ hinweisAnker: clipId }),
  bereich: null,
  setBereich: (bereich) => set({ bereich }),
  nachrueckenUmschalten: () => {
    const an = !get().nachruecken;
    set({ nachruecken: an });
    try {
      localStorage.setItem(NACHRUECKEN, an ? 'an' : 'aus');
    } catch {
      /* Privater Modus: dann gilt es nur für diese Sitzung. */
    }
  },
  setRaster: (wahl) => {
    set({ raster: wahl });
    try {
      localStorage.setItem(RASTER, wahl);
    } catch {
      /* Privater Modus: dann gilt es nur für diese Sitzung. */
    }
  },
  setPegelWahl: (wahl) => {
    set({ pegelWahl: wahl });
    try {
      localStorage.setItem(PEGEL, wahl);
    } catch {
      /* Privater Modus: dann gilt es nur für diese Sitzung. */
    }
  },
  zeitformatWeiter: () => {
    const wahl = naechstesZeitFormat(get().zeitformat);
    set({ zeitformat: wahl });
    try {
      localStorage.setItem(ZEITFORMAT, wahl);
    } catch {
      /* Privater Modus: dann gilt es nur für diese Sitzung. */
    }
  },
  tonkurveUmschalten: (trackId) => {
    const jetzt = get().tonkurven;
    const neu = jetzt.includes(trackId) ? jetzt.filter((t) => t !== trackId) : [...jetzt, trackId];
    set({ tonkurven: neu });
    try {
      localStorage.setItem(TONKURVEN, JSON.stringify(neu));
    } catch {
      /* Privater Modus: dann gilt es nur für diese Sitzung. */
    }
  },
  spurHoeheGross: Math.max(SPUR_NORMAL, gespeicherteSpurHoehe()),
  setSpurHoehe: (px) => {
    const hoehe = Math.round(Math.min(SPUR_MAX, Math.max(SPUR_MIN, px)));
    try {
      localStorage.setItem('schnitt.spurhoehe', String(hoehe));
    } catch {
      // Ein Browser ohne Speicher merkt es sich eben nur für diese Sitzung.
    }
    // Was über der Flachmarke liegt, gilt als „die volle Höhe“ -- danach
    // sucht der Umschalter, wenn er sie zurückholt.
    set(hoehe >= SPUR_FLACH_BIS ? { spurHoehe: hoehe, spurHoeheGross: hoehe } : { spurHoehe: hoehe });
  },
  zeitleisteUmschalten: () => {
    const { spurHoehe, spurHoeheGross, setSpurHoehe } = get();
    setSpurHoehe(spurHoehe < SPUR_FLACH_BIS ? Math.max(SPUR_NORMAL, spurHoeheGross) : SPUR_FLACH);
  },
  queue: { running: 0, waiting: 0, secondsPerSecond: null },
  peers: [],
  connection: 'connecting',
  zugangWeg: false,
  offen: null,
  offenSatz: null,
  alteOffen: null,
  selection: [],
  playhead: 0,
  playing: false,
  monitorMuted: false,
  zoom: 60,
  multiSelect: false,
  uebergangsModus: false,
  keyframeWahl: null,
  einfuegeStelle: null,
  einfuegeSpur: null,
  platzieren: null,
  einfuegeMedium: null,
  einfuegeWarteschlange: null,
  hochladen: null,
  spurBlatt: null,
  bereichBlatt: null,
  griff: null,
  maskeEntwurf: null,
  clipboard: [],
  toasts: [],
  undoStack: [],
  redoStack: [],

  open: async (project, role, gastName = '') => {
    // Was beim letzten Mal nicht hinausging (S28). Die Nummern darin kommen
    // aus einer früheren Sitzung; der Zähler fängt bei null an. Also wird er
    // hinter die höchste gesetzt -- sonst trüge ein neues Paket dieselbe
    // Nummer wie ein wartendes, und eine Bestätigung nähme das Falsche heraus.
    const offen = offenLesen(project.id);
    for (const paket of offen.pakete) clientSeq = Math.max(clientSeq, paket.seq);
    set({
      project,
      gastName,
      role: (role as EditorState['role']) ?? 'viewer',
      // Mit dem, was noch aussteht, darüber (7.220): sonst zeigte ein neu
      // geladenes Telefon den Stand des Servers, und was es noch nicht
      // losgeworden war, sah man nirgends.
      timeline: mitOffenem(project.timeline, offen),
      rev: project.rev,
      offen,
      offenSatz: offenSatz(offen),
      alteOffen: null,
      selection: [],
      playhead: 0,
      ueberfahrenVon: null,
      undoStack: [],
      redoStack: [],
      // Ein Modus gehört dem Projekt, in dem man ihn eingeschaltet hat: sonst
      // steht das nächste Projekt beim Öffnen im Übergangs-Modus, ohne dass
      // ihn jemand gewählt hat (gefunden bei der Durchsicht am
      // 11. September).
      uebergangsModus: false,
      platzieren: null,
    });
    await Promise.all([get().refreshAssets(), get().refreshRenders(), get().refreshKommentare()]);
    connect(project.id, set, get);
  },

  close: () => {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    socket?.close(1000, 'left the project');
    socket = null;
    set({ project: null, peers: [], connection: 'connecting', zugangWeg: false, playing: false, uebergangsModus: false });
  },

  dispatch: (input) => {
    let ops = Array.isArray(input) ? input : [input];
    if (ops.length === 0) return;
    const state = get();
    if (state.role === 'viewer') {
      state.toast('error', 'Du hast in diesem Projekt nur Leserechte.');
      return;
    }
    // Gesperrte Spuren: hier und nirgends sonst. Eine Sperre, die nur die
    // Zeitleiste beachtet, käme über die Werkzeugleiste, die Tastatur oder die
    // Anfasser trotzdem durch.
    const { erlaubt, gesperrt } = ohneGesperrte(state.timeline, ops);
    if (gesperrt > 0) {
      state.toast('info', gesperrt === ops.length ? 'Die Spur ist gesperrt.' : 'Ein Teil davon liegt auf einer gesperrten Spur.');
      if (erlaubt.length === 0) return;
    }
    ops = erlaubt;
    // **Magnetische Zeitleiste** (7.201): was die Änderung auf der Hauptspur
    // an Lücken reisst oder an Überdeckung schafft, zieht der Magnet glatt --
    // als gewöhnliche Schritte hinten an derselben Änderung. Ein Rückgängig
    // nimmt beides zurück, und alle anderen sehen dieselben Schritte.
    if (ops.some((op) => MAGNET_ANLAESSE.has(op.op))) {
      const nachher = applyOps(state.timeline, ops);
      if (nachher.settings.magnetisch) ops = [...ops, ...magnetZug(state.timeline, nachher)];
    }

    // Compute the undo entry against the state before the change, and reverse
    // it so undoing a batch unwinds it back to front.
    const inverse = ops
      .map((op) => invertOp(state.timeline, op))
      .filter((op): op is Op => op !== null)
      .reverse();
    // The label names the original change, not its reverse: "Clip verschoben"
    // reads the same on the undo and on the redo button.
    const entry: HistoryEntry = { ops: inverse, label: describeOps(ops) };

    set({
      timeline: applyOps(state.timeline, ops),
      undoStack: inverse.length ? [...state.undoStack, entry].slice(-100) : state.undoStack,
      redoStack: [],
    });
    sendOps(ops, set, get);
    gleichLaut(ops, entry, set, get);
  },

  undo: () => {
    const state = get();
    const entry = state.undoStack.at(-1);
    if (!entry) return;
    const redo = entry.ops
      .map((op) => invertOp(state.timeline, op))
      .filter((op): op is Op => op !== null)
      .reverse();
    set({
      timeline: applyOps(state.timeline, entry.ops),
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, { ops: redo, label: entry.label }].slice(-100),
    });
    sendOps(entry.ops, set, get);
  },

  redo: () => {
    const state = get();
    const entry = state.redoStack.at(-1);
    if (!entry) return;
    const undo = entry.ops
      .map((op) => invertOp(state.timeline, op))
      .filter((op): op is Op => op !== null)
      .reverse();
    set({
      timeline: applyOps(state.timeline, entry.ops),
      redoStack: state.redoStack.slice(0, -1),
      undoStack: [...state.undoStack, { ops: undo, label: entry.label }].slice(-100),
    });
    sendOps(entry.ops, set, get);
  },

  toggleSelect: (id) => {
    const { selection, timeline } = get();
    // Wieder abwählen nimmt die ganze Gruppe heraus -- sonst bliebe ihr
    // halber Rest gewählt, und `select` holte ihn beim nächsten Mal zurück.
    const gruppe = new Set(mitGruppe(timeline, [id]));
    get().select(selection.includes(id) ? selection.filter((x) => !gruppe.has(x)) : [...selection, id]);
  },
  setMultiSelect: (on) => set({ multiSelect: on }),
  setUebergangsModus: (on) => set({ uebergangsModus: on }),
  setSpurBlatt: (spurBlatt) => set({ spurBlatt }),
  setBereichBlatt: (bereichBlatt) => set({ bereichBlatt }),
  ersetzen: null,
  setErsetzen: (ersetzen) => set({ ersetzen }),
  clipUmbenennen: null,
  setClipUmbenennen: (clipUmbenennen) => set({ clipUmbenennen }),
  clipAngaben: null,
  setClipAngaben: (clipAngaben) => set({ clipAngaben }),
  setGriff: (griff) => set({ griff }),
  setMaskeEntwurf: (maskeEntwurf) => set({ maskeEntwurf }),
  copySelection: () => {
    const { timeline, selection } = get();
    const clipboard: { clip: Clip; trackId: string }[] = [];
    for (const track of timeline.tracks) {
      for (const clip of track.clips) if (selection.includes(clip.id)) clipboard.push({ clip: structuredClone(clip), trackId: track.id });
    }
    set({ clipboard });
  },
  clearClipboard: () => set({ clipboard: [] }),
  select: (roh) => {
    // Eine Gruppe wird ganz gewählt (S3). Hier und nicht an jeder Stelle, die
    // wählt: Antippen, Tastatur, Kopieren, Löschen und die Anwesenheit der
    // anderen gehen alle durch diese eine Tür, und eine Gruppe, die beim
    // Löschen zerfällt, wäre keine.
    const ids = mitGruppe(get().timeline, roh);
    // Wer einen anderen Clip wählt, hat den Keyframe von vorhin nicht mehr
    // gemeint. Nur wenn derselbe Clip gewählt bleibt, überlebt die Wahl.
    const wahl = get().keyframeWahl;
    set({
      selection: ids,
      ...(ids.length === 0 ? { multiSelect: false } : {}),
      ...(wahl && !(ids.length === 1 && ids[0] === wahl.clipId) ? { keyframeWahl: null } : {}),
    });
    schedulePresence(get);
  },
  waehleKeyframe: (keyframeWahl) => set({ keyframeWahl }),
  // Eine neue Stelle vergisst die Spur der vorigen; wer eine meint, setzt sie
  // danach (`setEinfuegeSpur`, 7.215). Sonst die erste Spur ihrer Art.
  setEinfuegeStelle: (einfuegeStelle) => set({ einfuegeStelle, einfuegeSpur: null }),
  setEinfuegeSpur: (einfuegeSpur) => set({ einfuegeSpur }),
  setPlatzieren: (platzieren) => set({ platzieren }),
  setEinfuegeMedium: (einfuegeMedium) => set({ einfuegeMedium }),
  setEinfuegeWarteschlange: (einfuegeWarteschlange) => set({ einfuegeWarteschlange }),
  setHochladen: (hochladen) => set({ hochladen }),

  setPlayhead: (seconds, broadcast = true) => {
    set({ playhead: Math.max(0, seconds) });
    if (broadcast) schedulePresence(get);
  },

  ueberfahrenVon: null,
  ueberfahren: (seconds) => {
    const s = get();
    if (s.playing) return;
    // Nur der eigene Schirm: die anderen sehen den Kopf, wo er wirklich steht.
    set({ ueberfahrenVon: s.ueberfahrenVon ?? s.playhead, playhead: Math.max(0, seconds) });
  },
  ueberfahrenEnde: (behalten) => {
    const von = get().ueberfahrenVon;
    if (von === null) return;
    // Läuft schon die Wiedergabe (Leertaste beim Überfahren), bleibt sie,
    // wo sie ist -- zurückspringen hiesse, mitten im Abspielen zu springen.
    if (behalten || get().playing) {
      set({ ueberfahrenVon: null });
      schedulePresence(get);
    } else set({ ueberfahrenVon: null, playhead: von });
  },

  setPlaying: (playing) => {
    set({ playing });
    // Während der Wiedergabe schreibt der Kopf sechzigmal je Sekunde, und
    // zwar absichtlich *ohne* zu senden -- sonst wäre die Verbindung nur noch
    // Kopfstand. Nur: die anderen sahen den Strich dann gar nicht mehr
    // wandern, er blieb stehen, wo das Abspielen begann. (Sascha, 6.
    // September: „drücke ich Play, geht der rote Marker leider nicht mit“.)
    // Viermal je Sekunde reicht dem Auge, und der Strich gleitet.
    if (abspielTakt) {
      clearInterval(abspielTakt);
      abspielTakt = null;
    }
    if (playing) abspielTakt = setInterval(() => schedulePresence(get), 250);
    // Und beim Anhalten einmal der genaue Stand, damit der Strich dort steht,
    // wo der Kopf wirklich stehen geblieben ist.
    else schedulePresence(get);
  },
  setMonitorMuted: (monitorMuted) => set({ monitorMuted }),
  // Bis 0,1 Punkte je Sekunde (S40): „Alles zeigen“ legt auch einen langen
  // Film ins Bild -- 10 Minuten am Telefon brauchen etwa 0,3.
  setZoom: (zoom) => set({ zoom: Math.min(400, Math.max(0.1, zoom)) }),

  refreshAssets: async () => {
    const project = get().project;
    if (!project) return;
    const nochAktuell = marke(`assets:${project.id}`);
    const { assets } = await api.assets(project.id);
    if (!nochAktuell()) return;
    set({ assets });
  },

  refreshKommentare: async () => {
    const project = get().project;
    if (!project) return;
    const nochAktuell = marke(`kommentare:${project.id}`);
    try {
      const { kommentare, neu } = await api.kommentare(project.id);
      // Eine überholte Antwort wird weggeworfen (7.113) -- sonst setzt der
      // Abruf vom Öffnen des Projekts den Stand von *vor* dem eigenen
      // Hinweis, nachdem der Abruf danach ihn schon gebracht hatte.
      if (!nochAktuell()) return;
      set({ kommentare, neueHinweise: neu ?? 0, kommentareFehler: false });
    } catch {
      /**
       * Hier stand `.catch(() => ({ kommentare: [], neu: 0 }))`.
       *
       * Ein Fehlschlag hat damit die Liste *geleert* -- und das sieht aus wie
       * Datenverlust. Genau so hat Sascha es gemeldet (8. September, in der
       * App): „Hinweise werden nicht gespeichert und auch nicht angezeigt.“
       * Gespeichert waren sie; abgeholt wurden sie nicht, und der Fehlschlag
       * war unsichtbar.
       *
       * Der Fangarm muss sein: der Abruf läuft beim Öffnen eines Projekts
       * mit, und ein klemmender Hinweis-Abruf darf das Öffnen nicht
       * verhindern. Nur darf er nichts wegwerfen und nichts verschweigen --
       * was da ist, bleibt stehen, und das Blatt sagt es.
       */
      if (!nochAktuell()) return;
      set({ kommentareFehler: true });
    }
  },

  /** „Ich habe sie gesehen“ -- danach zählt nur noch, was danach kommt. */
  hinweiseGesehen: async () => {
    const project = get().project;
    if (!project) return;
    await api.kommentareGesehen(project.id).catch(() => undefined);
    set({ neueHinweise: 0 });
  },

  refreshRenders: async () => {
    const project = get().project;
    if (!project) return;
    const nochAktuell = marke(`renders:${project.id}`);
    const { renders, queue } = await api.renders(project.id);
    if (!nochAktuell()) return;
    set({ renders, queue });
  },

  /** Re-reads the project row without touching the timeline the socket owns. */
  alteNachtragen: () => {
    set({ alteOffen: null });
    const nach = offenNachschicken(set, get, true);
    if (nach > 0) get().toast('info', 'Die alten Änderungen werden nachgetragen.');
  },

  alteVerwerfen: async () => {
    const current = get().project;
    if (!current) return;
    const schritte = get().alteOffen?.schritte ?? 0;
    const { schlange } = aufraeumen(get().offen ?? leereSchlange(current.id), Date.now());
    offenSchreiben(schlange);
    // Der eigene Stand trug das Alte; ohne es gilt wieder der des Servers,
    // mit dem, was danach kam, darüber.
    const { project } = await api.project(current.id);
    set({
      offen: schlange,
      offenSatz: offenSatz(schlange),
      alteOffen: null,
      timeline: mitOffenem(project.timeline, schlange),
      rev: project.rev,
    });
    offenNachschicken(set, get, true);
    get().toast('info', schritte === 1 ? 'Eine alte Änderung verworfen.' : `${schritte} alte Änderungen verworfen.`);
  },

  refreshProject: async () => {
    const current = get().project;
    if (!current) return;
    const { project } = await api.project(current.id);
    set({ project: { ...project, timeline: get().timeline } });
  },

  toast: (kind, message) => {
    const id = ++toastId;
    // Three at most, newest wins: four stacked messages cover the preview
    // and whatever menu the person is looking at.
    set((s) => ({ toasts: [...s.toasts, { id, kind, message }].slice(-3) }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), kind === 'error' ? 8000 : 4000);
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  clipById: (id) => {
    for (const track of get().timeline.tracks) {
      const clip = track.clips.find((c) => c.id === id);
      if (clip) return { clip, trackId: track.id };
    }
    return null;
  },
}));

declare global {
  interface Window {
    /** Was die App gerade gewählt hat, als JSON-Liste von Clip-Kennungen. */
    __auswahl?: () => string;
  }
}

/**
 * Was gewählt ist -- gefragt wird die App, nicht das Bild.
 *
 * Ein Instrument, wie `window.__ton` und `window.__puffer`: es läuft immer
 * mit, weil eines, das man erst einbauen muss, im Ernstfall nicht da ist.
 *
 * Der Grund steht in 7.117. Der Durchgang las die Auswahl bisher an einer
 * Klasse in der Zeitleiste ab, und „nichts“ hiess dort dreierlei: nichts
 * gewählt, der gewählte Clip steht gerade nicht im Dokument, oder die
 * Zeitleiste hat noch nicht neu gezeichnet. Drei verschiedene Fehler unter
 * einem Wort, und keiner davon liess sich aus der Meldung ablesen.
 */
if (typeof window !== 'undefined') {
  window.__auswahl = () => JSON.stringify(useEditor.getState().selection);
}

/**
 * Schritte hinausschicken -- und was nicht hinausgeht, aufbewahren (S28).
 *
 * Bis zum 17. September stand hier nur das `if`: war die Leitung nicht offen,
 * fiel der Schritt lautlos zu Boden. Auf dem Schirm war er getan, im
 * Rückgängig stand er, beim Server nie -- und beim nächsten Verbinden kam der
 * Stand des Servers und räumte ihn weg.
 *
 * Jetzt wird **immer** angestellt und erst dann geschickt. Die Bestätigung des
 * Servers (sein Echo mit derselben Nummer) nimmt das Paket wieder heraus; was
 * stehen bleibt, geht beim Verbinden noch einmal hinaus.
 */
/**
 * **Neue Clips gleich laut** (7.210). Kommt ein Clip mit Ton *aus einer
 * Datei* auf die Zeitleiste -- aus der Mediathek, dem Einfüge-Blatt oder als
 * Sprachaufnahme --, holt das hier seine Wellenform und setzt den Pegel, den
 * „Normalisieren“ setzen würde (`autoPegel`). Welche das sind, sagt die
 * Stelle, die sie einfügt (`ausDatei`): Trennen, Duplizieren und „Pausen
 * raus“ legen ebenfalls neue Clips an, und deren Stücke einzeln anzugleichen
 * hiesse, eine Aufnahme in verschieden laute Teile zu zerlegen.
 *
 * Der Pegel hängt sich an den Schritt des Einfügens, solange der noch oben
 * liegt: ein Rückgängig nimmt beides zurück. Kam dazwischen ein anderer
 * Schritt, wird er ein eigener. Liegt die Wellenform noch nicht vor (eine
 * Aufnahme, die gerade eingelesen wird), wird eine Minute lang nachgesehen.
 */
const ausDateiOps = new WeakSet<Op>();
export function ausDatei<T extends Op>(op: T): T {
  ausDateiOps.add(op);
  return op;
}

function gleichLaut(ops: Op[], entry: HistoryEntry, set: Setter, get: () => EditorState): void {
  const neu = ops.filter((op): op is Extract<Op, { op: 'addClip' }> => op.op === 'addClip' && ausDateiOps.has(op));
  if (neu.length === 0 || get().timeline.settings.autoNormalisieren === false) return;
  for (const op of neu) {
    const quelle = op.clip.source;
    if (quelle.kind !== 'asset') continue;
    const assetId = quelle.assetId;
    const versuch = async (rest: number): Promise<void> => {
      const url = get().assets.find((a) => a.id === assetId)?.waveformUrl;
      if (!url) {
        if (rest > 0) setTimeout(() => void versuch(rest - 1), 3000);
        return;
      }
      const welle = await fetch(url).then((r) => (r.ok ? (r.json() as Promise<Wellenform>) : null));
      if (!welle) return;
      const st = get();
      const clip = st.timeline.tracks.flatMap((t) => t.clips).find((c) => c.id === op.clip.id);
      const pegel = clip ? autoPegel(clip, welle) : null;
      if (!clip || pegel === null) return;
      const schritt = { op: 'updateClip', clipId: clip.id, patch: { gain: pegel } } as Op;
      const zurueck = invertOp(st.timeline, schritt);
      if (st.undoStack.at(-1) === entry && zurueck) {
        entry.ops.unshift(zurueck);
        set({ timeline: applyOps(st.timeline, [schritt]) });
        sendOps([schritt], set, get);
      } else st.dispatch(schritt);
    };
    void versuch(20).catch(() => undefined);
  }
}

function sendOps(ops: Op[], set: Setter, get: () => EditorState): void {
  const projectId = get().project?.id;
  if (!projectId) return;
  const { seq, klient } = naechstesPaket(projectId);
  const paket = { seq, wann: Date.now(), ops };
  const { schlange, verloren } = anstellen(get().offen ?? leereSchlange(projectId), paket);
  if (verloren > 0) {
    // Ein stiller Verlust ist genau der Fehler, den diese Datei behebt.
    get().toast(
      'error',
      `Zu viel liegt ungesichert an: ${verloren} ältere Änderung${verloren === 1 ? '' : 'en'} ist verloren.`,
    );
  }
  offenSchreiben(schlange);
  set({ offen: schlange, offenSatz: offenSatz(schlange) });
  // Steht die Frage nach dem Alten noch aus, wartet auch das Neue (7.220):
  // es käme sonst vor dem Alten an, auf dem es aufbaut.
  if (socket?.readyState === WebSocket.OPEN && !get().alteOffen) {
    socket.send(JSON.stringify({ type: 'ops', ops, clientSeq: paket.seq, klient }));
  }
}

/** Was steht, noch einmal hinausschicken -- in der Reihenfolge, in der es entstand. */
function offenNachschicken(set: Setter, get: () => EditorState, auchAltes = false): number {
  const projectId = get().project?.id;
  if (!projectId || socket?.readyState !== WebSocket.OPEN) return 0;
  const schlange = get().offen ?? leereSchlange(projectId);
  // Zu Altes geht nicht von selbst hinaus -- aber auch nicht verloren
  // (7.220). Bis zum 4. Oktober wurde es hier verworfen, mit einer Meldung,
  // die acht Sekunden stand.
  const alt = alteSchritte(schlange, Date.now());
  if (!auchAltes && alt.schritte > 0) {
    set({ alteOffen: { schritte: alt.schritte, seit: alt.seit! } });
    return 0;
  }
  for (const paket of schlange.pakete) {
    socket.send(JSON.stringify({ type: 'ops', ops: paket.ops, clientSeq: paket.seq, klient: klientId(projectId) }));
  }
  return schlange.pakete.length;
}

/** Presence is chatty; one update every 150 ms is plenty to follow a cursor. */
/** Läuft, solange abgespielt wird: der Kopf sendet sonst gar nicht. */
let abspielTakt: ReturnType<typeof setInterval> | null = null;

function schedulePresence(get: () => EditorState): void {
  if (presenceTimer) return;
  presenceTimer = setTimeout(() => {
    presenceTimer = null;
    if (socket?.readyState !== WebSocket.OPEN) return;
    const { playhead, selection } = get();
    socket.send(JSON.stringify({ type: 'presence', playhead, selection }));
  }, 150);
}

type Setter = (partial: Partial<EditorState> | ((s: EditorState) => Partial<EditorState>)) => void;

function connect(projectId: string, set: Setter, get: () => EditorState): void {
  socket?.close();
  set({ connection: 'connecting' });

  const share = getShareToken();
  const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${protocol}://${location.host}/ws/projects/${projectId}${share ? `?share=${encodeURIComponent(share)}` : ''}`;
  const ws = new WebSocket(url);
  socket = ws;

  ws.onopen = () => {
    reconnectDelay = 1000;
    set({ connection: 'live' });
    // Was während der Unterbrechung entstand, geht jetzt hinaus (S28).
    const nach = offenNachschicken(set, get);
    if (nach > 0) get().toast('info', `${nach} ungesicherte Änderung${nach === 1 ? '' : 'en'} wird nachgetragen.`);
  };

  ws.onmessage = (event: MessageEvent<string>) => {
    let message: ServerMessage;
    try {
      message = JSON.parse(event.data) as ServerMessage;
    } catch {
      return;
    }
    handleServerMessage(message, set, get);
  };

  ws.onclose = (event: CloseEvent) => {
    if (socket !== ws) return;
    set({ connection: 'offline' });
    // 1011 means the server will refuse the same way next time. Reconnecting
    // would be a silent loop in which nothing anybody edits is ever saved, so
    // say so once and stop trying.
    if (event.code === 1011) {
      get().toast('error', event.reason || 'Die Verbindung wurde vom Server abgelehnt.');
      return;
    }
    // Der Zugang ist weg -- Link widerrufen oder aus dem Projekt genommen
    // (7.280). Neu verbinden hiesse, gegen eine Tür zu laufen; also sagen,
    // was ist, und aufhören. 4403 ist der Code, den der Server dafür setzt.
    if (event.code === 4403) {
      set({ zugangWeg: true });
      get().toast('error', event.reason || 'Dein Zugang zu diesem Projekt wurde zurückgenommen.');
      return;
    }
    // Exponential backoff so a phone waking from sleep does not hammer the API.
    reconnectTimer = setTimeout(() => {
      reconnectDelay = Math.min(reconnectDelay * 2, 20_000);
      if (get().project?.id === projectId) connect(projectId, set, get);
    }, reconnectDelay);
  };

  ws.onerror = () => ws.close();
}

function handleServerMessage(message: ServerMessage, set: Setter, get: () => EditorState): void {
  switch (message.type) {
    case 'snapshot': {
      const local = get();
      // Only take the server's timeline when we have nothing newer of our own;
      // otherwise a reconnect mid-edit would drop the last few changes.
      //
      // Und nicht, solange etwas aussteht (S28): der Stand des Servers sieht
      // beim Verbinden immer „neuer“ aus, weil die eigene Nummer nur mit
      // seinen Bestätigungen wächst. Genau daran gingen die Schritte einer
      // Unterbrechung verloren -- lautlos.
      const schlange = local.offen ?? leereSchlange(local.project?.id ?? '');
      if (serverStandUebernehmen(schlange, message.rev, local.rev)) {
        set({ timeline: message.timeline, rev: message.rev });
      }
      set({ peers: message.peers, connection: 'live' });
      break;
    }
    case 'ops': {
      const local = get();
      // Our own operations are already applied optimistically.
      const isEcho = message.clientSeq !== undefined;
      // Das Echo ist die Bestätigung: das Paket ist beim Server (S28).
      if (isEcho && local.offen) {
        const schlange = bestaetigt(local.offen, message.clientSeq!);
        offenSchreiben(schlange);
        set({ offen: schlange, offenSatz: offenSatz(schlange) });
      }
      const timeline = isEcho ? local.timeline : message.ops.reduce(applyOp, local.timeline);
      // Nimmt jemand anderes einen gewählten Clip weg, bleibt er nicht
      // gewählt: sonst steht die Clip-Leiste für etwas, das es nicht mehr
      // gibt (gefunden beim Durchlauf zu 7.66a).
      const da = new Set(timeline.tracks.flatMap((t) => t.clips.map((c) => c.id)));
      const selection = local.selection.filter((id) => da.has(id));
      set({
        rev: Math.max(local.rev, message.rev),
        timeline,
        ...(selection.length !== local.selection.length ? { selection } : {}),
      });
      break;
    }
    case 'presence':
      set({ peers: message.peers });
      break;
    case 'job':
      void handleJobEvent(message.job, get);
      break;
    case 'kommentare':
      // Nur das Signal, nicht der Inhalt: wer liest, holt selbst -- dann
      // gelten dieselben Rechte wie sonst, auch für einen Gast am Link.
      void get().refreshKommentare();
      break;
    case 'error':
      get().toast('error', message.message);
      break;
    case 'rolle':
      // Die Rolle gilt sofort (7.281): was man nicht mehr darf, verschwindet
      // aus den Werkzeugen, statt erst beim nächsten Versuch abgewiesen zu
      // werden.
      set({ role: message.role });
      get().toast('info', message.message);
      break;
    case 'pong':
      break;
  }
}

async function handleJobEvent(
  job: { id: string; kind: string; status: string; progress: number; error?: string },
  get: () => EditorState,
): Promise<void> {
  const state = get();
  if (job.kind === 'asset') {
    await state.refreshAssets();
    if (job.status === 'failed') {
      // Just the summary line; the whole message is on the row in the media
      // sheet, where there is room for it.
      const reason = (job.error ?? '').split('\n')[0] ?? '';
      state.toast('error', `Ein Medium konnte nicht verarbeitet werden. ${reason}`.trim().slice(0, 200));
    }
  } else if (job.kind === 'render') {
    await state.refreshRenders();
    // Die Abschnitte der Vorschau rechnen im Hintergrund und melden sich
    // nicht (7.71b) -- sonst poppt bei einem langen Schnitt zwanzigmal „Der
    // Export ist fertig“ auf. Gemeldet wird, was jemand ausdrücklich wollte.
    const fertig = get().renders.find((r) => r.id === job.id);
    if (fertig?.kind === 'preview' && fertig.abdruck) return;
    if (job.status === 'ready') state.toast('success', 'Der Export ist fertig.');
    if (job.status === 'failed') state.toast('error', `Export fehlgeschlagen: ${job.error ?? ''}`);
  } else if (job.kind === 'transcript') {
    if (job.status === 'ready') state.toast('success', 'Das Transkript ist fertig.');
    if (job.status === 'failed') state.toast('error', `Transkription fehlgeschlagen: ${job.error ?? ''}`);
  }
}
